// EAGER-safe async decode API — THE surface every map/peek uses (never the sync core directly). It
// awaits loadSpatialDecoder() (dynamic import of the decode chunk), decodes in `chunkSize` slices,
// and yields to the event loop between slices so no main-thread stall exceeds the ~100 ms budget.
// The signature is worker-ready by design: the Checkpoint-2 worker decision swaps ONLY this module's
// internals (chunk loop → worker pool); downstream consumers never change.

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { SpatialDecodeResult, SpatialDeclarationDecodeInput } from './types';
import { MAX_EAGER_DECODE_BYTES } from './decodeLimits';
import { loadSpatialDecoder } from './loadSpatialDecoder';

/**
 * `declaration` (Task 18) is an ADDITIVE optional field — the frozen native
 * callers omit it and are unaffected. When present, the batch routes to
 * `decodeDeclaredSpatialValue` instead of the per-engine wire decoder; both
 * paths return the same frozen `SpatialDecodeResult` tagged union. Its type is
 * the decode-local structural input, which the renderer `SpatialColumnDeclaration`
 * satisfies — so callers pass the full declaration.
 */
export type SpatialBatchItem = {
  key: string | number;
  engine: DatabaseEngineId;
  value: unknown;
  dataType?: string;
  declaration?: SpatialDeclarationDecodeInput;
};
export type SpatialBatchResult = { key: string | number; result: SpatialDecodeResult };
export type DecodeBatchOptions = {
  signal?: AbortSignal; // aborts between chunks → rejects with an 'AbortError'
  chunkSize?: number; // default 500
  maxBytesPerValue?: number; // default MAX_EAGER_DECODE_BYTES; single-value views pass the on-demand cap
  onProgress?: (done: number, total: number) => void;
};

function abortError(): Error {
  if (typeof DOMException !== 'undefined') {
    return new DOMException('The spatial decode batch was aborted.', 'AbortError');
  }
  const error = new Error('The spatial decode batch was aborted.');
  error.name = 'AbortError';
  return error;
}

export async function decodeSpatialBatch(
  items: SpatialBatchItem[],
  opts: DecodeBatchOptions = {},
): Promise<SpatialBatchResult[]> {
  const { signal, chunkSize = 500, maxBytesPerValue = MAX_EAGER_DECODE_BYTES, onProgress } = opts;
  if (signal?.aborted) throw abortError();

  const { decodeSpatialValue, decodeDeclaredSpatialValue } = await loadSpatialDecoder();
  const results: SpatialBatchResult[] = [];
  const total = items.length;

  for (let i = 0; i < total; i += chunkSize) {
    if (signal?.aborted) throw abortError();
    const slice = items.slice(i, i + chunkSize);
    for (const item of slice) {
      results.push({
        key: item.key,
        result: item.declaration
          ? decodeDeclaredSpatialValue(item.declaration, item.value, { maxBytes: maxBytesPerValue })
          : decodeSpatialValue(item.engine, item.value, item.dataType, { maxBytes: maxBytesPerValue }),
      });
    }
    onProgress?.(results.length, total);
    if (i + chunkSize < total) await new Promise((resolve) => setTimeout(resolve, 0));
  }

  return results;
}
